// Shared verbatim Terms of Use, Disclaimer and Privacy Notice.
// Source of truth: EYB Consulting Pty Ltd document, effective 10/01/2026.
// Used by ClientSignupPage and RegisterPage. DO NOT edit wording without
// also updating the source document.

export const TERMS_TEXT = `Terms of Use, Disclaimer and Privacy Notice
EYB Consulting Pty Ltd
ABN 43 677 737 165
PO Box 139, Flinders Lane, VIC 8009, Australia

Effective date: 10/01/2026

1. Acceptance of Terms
By accessing, registering for, or using this platform, application, software, or any related tools (collectively, the Platform), you confirm that you have read, understood, and agree to be bound by these Terms of Use, Disclaimer, and Privacy Notice. If you do not agree, you must not access or use the Platform.

2. Nature and Purpose of the Platform
The Platform is provided by EYB Consulting Pty Ltd (EYB) as a business planning, cash flow modelling, scenario analysis, and financial education tool only. The Platform:
  - Relies entirely on user-entered data and assumptions
  - Produces indicative outputs for planning and discussion purposes
  - Does not validate, audit, or verify information

The Platform does not provide financial advice, legal advice, taxation advice, accounting advice, credit advice, or investment advice.

3. No Financial Product Advice
EYB does not hold an Australian Financial Services Licence (AFSL). Nothing in the Platform constitutes financial product advice under the Corporations Act 2001 ( Cth ), nor considers your objectives, financial situation, or needs. You must obtain independent professional advice before acting on any information generated by the Platform.

4. User Responsibility and Accuracy of Inputs
You acknowledge and agree that:
  - All data entered is your sole responsibility
  - Outputs are only as accurate as the inputs and assumptions used
  - EYB does not review, check, or correct user data

You remain fully responsible for:
  - Verifying outputs
  - Decisions made using the Platform
  - Any reliance placed on forecasts, projections, or scenarios

5. No Guarantee of Outcomes
EYB gives no assurance or guarantee regarding:
  - Business performance
  - Cash flow outcomes
  - Profitability or solvency
  - Funding approval, loan availability, or investment outcomes

Forecasts and scenarios are indicative only and may not reflect actual results.

6. Limitation of Liability
To the maximum extent permitted by law, EYB excludes all liability for any loss or damage (including indirect, consequential, economic, or special loss) arising from or in connection with:
  - Use of the Platform
  - Reliance on outputs
  - Errors, omissions, or assumptions
  - Business or financial decisions made by the user

Where liability cannot be excluded, EYB\u2019s liability is limited to the amount paid by the user (if any) to access the Platform in the twelve months preceding the claim.

7. No Liability of Individuals
You agree that no claim may be brought against any:
  - Director
  - Officer
  - Employee
  - Contractor
  - Consultant
  - Advisor
  - Agent or representative of EYB Consulting Pty Ltd.

Any liability, to the extent it exists, is limited solely to EYB Consulting Pty Ltd as a corporate entity.

8. Third-Party Reliance
If you share Platform outputs with third parties (including banks, lenders, investors, or partners):
  - EYB is not responsible for how those parties interpret or rely on the information
  - Outputs must not be represented as audited, verified, or endorsed by EYB
  - Third-party reliance is entirely at their own risk

9. Technology, Availability and Errors
EYB does not warrant that the Platform will be uninterrupted, error-free, secure, or always available. EYB may modify, suspend, or discontinue the Platform at any time without notice.

10. Privacy, Data Storage and Security

10.1 Data Collection and Use
By using the Platform, you consent to EYB collecting, storing, processing, and analysing information voluntarily entered by you for the purpose of providing Platform functionality, including but not limited to:
  - Cash flow forecasting
  - Scenario modelling
  - Business planning
  - Financial reporting
  - File storage and document management
  - Xero and third-party integrations

EYB may use aggregated and de-identified data internally for platform improvement, analytics, performance monitoring, and system enhancement purposes. EYB does not use your data to provide personal financial product advice, investment advice, taxation advice, or credit advice.

10.2 Cloud Infrastructure and Data Hosting
The Platform utilises third-party cloud infrastructure providers and technology platforms for hosting, processing, storage, backup, and operational functionality. This may include infrastructure providers such as:
  - Amazon Web Services (AWS)
  - Secure cloud storage services
  - Database providers
  - Integration providers
  - Email and notification providers
  - Analytics and monitoring providers

Where reasonably practicable, EYB intends to utilise Australian-hosted infrastructure and storage environments. However, certain operational services, integrations, backups, metadata, logs, or supporting technologies may involve processing across multiple jurisdictions. By using the Platform, you consent to the transfer, storage, processing, and handling of your information through these third-party systems and providers.

10.3 Xero and Third-Party Integrations
Where you connect the Platform to third-party systems, including Xero or similar accounting platforms:
  - You authorise EYB and the Platform to access the data you choose to connect
  - EYB relies entirely on information provided through those integrations
  - EYB does not independently verify the accuracy, completeness, or integrity of integrated data
  - You remain responsible for reviewing and validating all imported information

EYB is not responsible for:
  - Errors in third-party systems
  - API failures
  - Synchronisation issues
  - Delays in data updates
  - Incorrect calculations arising from external data sources

10.4 Uploaded Documents and Files
You acknowledge that documents, files, spreadsheets, reports, and other materials uploaded into the Platform are stored electronically using third-party infrastructure providers. You are solely responsible for:
  - The legality of uploaded content
  - Ensuring you have authority to upload the information
  - Maintaining independent backups where required

You must not upload:
  - Illegal material
  - Malware or harmful code
  - Content that infringes intellectual property rights
  - Sensitive personal information unless necessary and accepted at your own risk

10.5 AI Generated Insights and Outputs
Certain Platform functionality may utilise automated processing, forecasting logic, artificial intelligence, machine learning, or algorithmic analysis. Any:
  - Recommendations
  - Forecasts
  - Insights
  - Commentary
  - Alerts
  - Summaries
  - Generated reports

are indicative only and should not be relied upon as professional advice or guarantees of performance or outcomes. AI and automated systems may produce inaccurate, incomplete, outdated, or misleading information. You must independently assess all outputs before making business, financial, funding, operational, or strategic decisions.

10.6 Cybersecurity and System Risk
While EYB may implement reasonable administrative, technical, and organisational measures designed to protect Platform data, you acknowledge that:
  - No internet-based platform can be guaranteed to be fully secure
  - Cybersecurity threats continually evolve
  - Unauthorised access, data breaches, ransomware attacks, or service interruptions may occur despite reasonable safeguards

To the maximum extent permitted by law, EYB excludes liability for:
  - Cyber incidents
  - Hacking
  - Malware
  - Data interception
  - Service outages
  - Loss or corruption of data
  - Third-party infrastructure failures

10.7 User Responsibility for Security
You are responsible for:
  - Maintaining secure passwords and login credentials
  - Restricting unauthorised access to your account
  - Ensuring devices used to access the Platform are secure
  - Maintaining your own backups where necessary

You must notify EYB immediately if you suspect unauthorised access to your account.

10.8 Data Retention and Deletion
EYB may retain, archive, suspend, remove, or delete data at its discretion, subject to legal and operational requirements. EYB does not guarantee:
  - Permanent storage
  - Indefinite retention
  - Retrieval of deleted data
  - Ongoing platform availability

You are responsible for exporting and retaining copies of important data, reports, and documents.

10.9 Business Continuity and Service Availability
The Platform may occasionally experience:
  - Downtime
  - Maintenance interruptions
  - Technical faults
  - Third-party outages
  - Feature modifications

EYB does not guarantee uninterrupted availability of the Platform and reserves the right to modify, suspend, restrict, or discontinue functionality at any time.

11. Intellectual Property
All intellectual property in the Platform, including models, logic, calculations, structure, and design, remains the property of EYB Consulting Pty Ltd. You are granted a limited, non-exclusive, non-transferable licence to use the Platform for internal business purposes only.

12. Prohibited Use
You must not:
  - Reverse engineer or resell the Platform
  - Misrepresent outputs as professional advice
  - Use the Platform for unlawful or misleading purposes
  - Use the Platform in a way that harms EYB\u2019s reputation

13. Indemnity
You agree to indemnify EYB Consulting Pty Ltd against any claims, losses, damages, or costs arising from:
  - Your use of the Platform
  - Your breach of these terms
  - Any third-party reliance on information you share

14. Governing Law
These Terms are governed by the laws of Victoria, Australia, and you submit to the exclusive jurisdiction of Victorian courts.

15. Electronic Communications
By using the Platform, you consent to receiving electronic communications from EYB, including:
  - Account notifications
  - Security alerts
  - Platform updates
  - System notices
  - Email communications relating to your use of the Platform

16. Amendments
EYB may amend these Terms at any time by publishing updated terms within the Platform or on its website. Continued use of the Platform after updates constitutes acceptance of the revised terms.

17. Acknowledgement
By clicking \u201CI Agree\u201D, creating an account, or logging in, you confirm that:
  - You understand the limitations of the Platform
  - You accept all risks associated with its use
  - You agree to be bound by these Terms of Use, Disclaimer, and Privacy Notice`;
